import React, { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Box, useTheme } from '@mui/material'

import { exportExamsAuditReport } from '../../../utils/auditing-export-utilities'
import TryAgainMessageBlock from '../../common/TryAgainMessageBlock'
import DataLoadingSpinner from '../../common/DataLoadingSpinner'
import AuditPageStatsFilterBar from '../AuditPageStatsFilterBar'
import CustomTable from '../../common/custom-table/CustomTable'
import LineBarGraph from '../../common/LineBarGraph'
import { Icons } from '../../../utils/utilities'
import { toast } from 'react-toastify'
import api from '../../../service/api'



const columns = [
    { id: 1, field: "index", index: true, name: 'Sl. No.', width: "60px", padding: "10px 20px" },
    { id: 2, filterable: true, field: "faculty_name", renderCell: row => ( row?.faculty_name || row?.employee_name || "" )?.toLowerCase() || row?.created_by, cellStyles: { textTransform: "capitalize" }, linkGetter: row => `/${window.location.pathname?.includes( 'admin' ) ? "admin" : "audit"}/faculty/${row.created_by}?legacy=${row.legacy_system_data}`, name: "Faculty", width: "150px", padding: "10px 20px" },
    { id: 3, filterable: true, field: "created_by", linkGetter: row => `/${window.location.pathname?.includes( 'admin' ) ? "admin" : "audit"}/faculty/${row.created_by}?legacy=${row.legacy_system_data}`, name: 'AUID', align: "left", padding: "10px 20px" },
    { id: 6, filterable: true, field: "institute", name: "Institute", width: "100px", padding: "10px 20px" },
    { id: 6, filterable: true, field: "department", renderCell: row => `${row.institute} - ${row.department?.toLowerCase()}`, cellStyles: { textTransform: "capitalize" }, name: "Department", width: "150px", padding: "10px 20px" },
    { id: 4, sortable: true, field: "count", dataType: "number", align: "center", name: "Exams", width: "150px", padding: "10px 20px" },
    { id: 5, sortable: true, field: "attempts", dataType: "number", align: "center", name: "Attempts", width: "150px", padding: "10px 20px" },
]

const ExamsStats = () => {

    const [searchParams] = useSearchParams( { sortByDept: "0", searchText: "", month: "", selectedDepartment: "all", selectedInstitute: "all", view: "tabular", sortByName: "1", sortByAttemptsCount: "0", sortByExamsCount: "0" } )
    const month = searchParams.get( 'month' ) || ""
    const view = searchParams.get( 'view' ) || "tabular"
    const selectedInstitute = searchParams.get( 'selectedInstitute' ) || "all"
    const selectedDepartment = searchParams.get( 'selectedDepartment' ) || "all"

    const [loading, setLoading] = useState( true )
    const [graphData, setGraphData] = useState( [] )
    const [statsError, setStatsError] = useState( null )
    const [graphError, setGraphError] = useState( null )
    const [facultyStats, setFacultyStats] = useState( [] )
    const [loadingGraphData, setLoadingGraphData] = useState( true )
    const [last12MonthsStats, setLast12MonthsStats] = useState( null )
    const [facultiesWithZeroExams, setFacultiesWithZeroExams] = useState( [] )
    const [facultiesWithExamsStats, setFacultiesWithExamsStats] = useState( [] )
    const [loadingZeroExamsFaculties, setLoadingZeroExamsFaculties] = useState( false )
    const [showZeroExamsFaculties, setShowZeroExamsFaculties] = useState( false )

    const { layout, palette } = useTheme()

    const filterFunc = item => {
        if ( selectedDepartment !== "all" ) {
            const [department_id, legacy_department_id] = selectedDepartment?.split( "-" )
            return ( item.legacy_system_data && +legacy_department_id === +item?.department_id ) || ( !item.legacy_system_data && +department_id === +item?.department_id )
        }
        return true
    }

    const handleShowFacultiesWithZeroExamsChange = async e => {
        if ( e.target.checked ) {
            try {
                setLoadingZeroExamsFaculties( true )
                const queries = {
                    institute: selectedInstitute !== "all" ? selectedInstitute : "",
                    department: selectedDepartment !== "all" ? selectedDepartment : ""
                }
                const { data: { data: facultiesList } } = await api.audit.fetchFacultiesUnderAuditor( queries )
                const facultiesIdWithExams = facultyStats.map( i => i.created_by )
                const facultiesIdWithoutExams = facultiesList.filter( i => !facultiesIdWithExams.includes( i.session_id ) ).map( i => {
                    return {
                        count: 0,
                        attempts: 0,
                        faculty_name: i.employee_name,
                        department: i.branch_name,
                        department_id: i.branch_id,
                        institute: i.institute_name_short,
                        created_by: i.session_id,
                    }
                } )
                setFacultiesWithZeroExams( facultiesIdWithoutExams )
                setShowZeroExamsFaculties( true )

            } catch ( err ) {
                console.log( err )
                toast( "Oops! Something went wrong while fetching faculties with no exams." )
            }
        } else {
            setFacultiesWithZeroExams( [] )
            setShowZeroExamsFaculties( false )
        }
        // setSearchParams( prev => {
        //     prev.set( 'sortByName', "0" )
        //     prev.set( 'sortByExamsCount', "0" )
        //     prev.set( 'sortByAttemptsCount', "0" )
        //     return prev
        // }, { replace: true } )
        setLoadingZeroExamsFaculties( false )
    }

    const getData = useCallback( async () => {
        setLoading( true )
        try {
            const { data: { data: { facultyStats = [], last12MonthsStats = {} } } } = await api.audit.getExamsStats( { month, institute: selectedInstitute !== "all" ? selectedInstitute : "" } )

            setLast12MonthsStats( Object.keys( last12MonthsStats ).length > 0 ? last12MonthsStats : null )
            setFacultiesWithExamsStats( facultyStats )
            setStatsError( null )
        } catch ( err ) {
            setStatsError( { code: err?.response?.status, message: err?.response?.data?.message || "Something went wrong while fetching the exams audit data." } )
        } finally {
            setLoading( false )
        }
    }, [month, selectedInstitute] )

    useEffect( () => {
        setFacultiesWithZeroExams( [] )
        setShowZeroExamsFaculties( false )
    }, [month] )


    const getGraphData = useCallback( async () => {
        try {
            const institute = selectedInstitute !== "all" ? selectedInstitute : ""
            const department = selectedDepartment !== "all" ? selectedDepartment : ""
            const { data: graphDetails } = await api.audit.getExamGraphData( { month, institute, department } )
            setGraphData( graphDetails.data )
            setGraphError( null )
        } catch ( err ) {
            setGraphError( { code: err?.response?.status, message: err?.response?.data?.message || "Something went wrong while fetching the exams graph data." } )
        } finally {
            setLoadingGraphData( false )
        }
    }, [month, selectedInstitute, selectedDepartment] )

    useEffect( () => {
        getData()
    }, [getData] )

    useEffect( () => {
        getGraphData()
    }, [getGraphData] )

    useEffect( () => {
        setFacultyStats( [...facultiesWithZeroExams, ...facultiesWithExamsStats] )
    }, [facultiesWithExamsStats, facultiesWithZeroExams] )

    const filtered = facultyStats.filter( filterFunc )

    return (
        <Box sx={{ flexShrink: 0, display: "flex", flexDirection: "column", gap: layout.cardsGap }}>

            {/* Filter */}
            <AuditPageStatsFilterBar loadingZeroQuizzesFaculties={loadingZeroExamsFaculties} showzeroassignmentsfaculties={showZeroExamsFaculties} handleShowFacultyWithZeroResourceChange={handleShowFacultiesWithZeroExamsChange} resource="exams" listResourceBtnLabel="View Exams" title="Exams statistics" />

            {!statsError && !loading && view === 'tabular' && <CustomTable
                globalSearch="Search by faculty name or employee code..."
                hasFilters={selectedDepartment !== "all" || selectedInstitute !== "all"}
                pageSize={50}
                columns={columns}
                data={filtered}
                tableStyles={{
                    maxHeight: "450px"
                }}
                customActions={[
                    {
                        type: "button",
                        label: "Export",
                        startIcon: Icons.default.Publish,
                        onClick: ( rows ) => exportExamsAuditReport( rows, last12MonthsStats, `${month || "complete"}-${selectedInstitute !== "all" ? selectedInstitute + "-" : ""}${selectedDepartment !== "all" ? selectedDepartment : "alldept"}-exams-audit-report` )
                    }
                ]}
            />}

            {statsError && <TryAgainMessageBlock err={statsError.message} loading={loading} getData={getData} code={statsError.code} />}

            {!loading && !loadingGraphData && view !== 'tabular' &&
                <Box display="flex" flexGrow={1} color={palette.common.font} minHeight="400px">
                    {!graphError ? <LineBarGraph barColor='#ffaa00' lineColor='#642547' showMenuBar={false} barLegend='Exam attempts' yAxisName="Exams" xAxisName="Date" y2AxisName="Attempts" lineLegend='Exams created' barValuesKey="attempts" barData={graphData} dateformat={"%Y-%m-%d"} xAxisValuesKey={'date'} lineValuesKey={'exams'} lineData={graphData} />
                        : <TryAgainMessageBlock err={graphError.message} loading={loadingGraphData} getData={getGraphData} code={graphError.code} />}
                </Box>
            }

            {( loading || loadingGraphData ) && !statsError && !graphError && <DataLoadingSpinner padding="0" waitingMessage="Loading exams audit report please..." />}
        </Box >
    )
}

export default ExamsStats
